import { Link, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import type { AdminGameListItem, GameKind, Paged } from '@mafia/core';
import { KindBadge, KindTabs } from '@/components/Kind';
import { api } from '@/lib/api';
import { formatDate, teamName } from '@/lib/format';
import { Alert, Badge, Card, Empty, Pagination, Spinner } from '@/components/ui';

const STATUSES = [
  { id: '', label: 'Все' },
  { id: 'IN_PROGRESS', label: 'Идут' },
  { id: 'FINISHED', label: 'Завершены' },
  { id: 'ABORTED', label: 'Прерваны' },
] as const;

export function AdminGames() {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const userId = params.get('userId') ?? '';
  const kind = (params.get('kind') ?? 'ALL') as GameKind | 'ALL';
  const page = Number(params.get('page') ?? 1);

  const query = new URLSearchParams({
    page: String(page),
    ...(status ? { status } : {}),
    ...(userId ? { userId } : {}),
    ...(kind !== 'ALL' ? { kind } : {}),
  });
  const games = useQuery({
    queryKey: ['admin', 'games', status, userId, kind, page],
    queryFn: () => api<Paged<AdminGameListItem>>(`/api/admin/games?${query}`),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {STATUSES.map((s) => (
          <button
            key={s.id}
            onClick={() => setParams((p) => (s.id ? p.set('status', s.id) : p.delete('status'), p.delete('page'), p))}
            className={clsx('rounded-lg px-3 py-2 text-sm', status === s.id ? 'bg-white/10 text-white' : 'text-zinc-400 hover:text-white')}
          >
            {s.label}
          </button>
        ))}
        <KindTabs
          value={kind}
          withAll
          onChange={(k) => setParams((p) => (k === 'ALL' ? p.delete('kind') : p.set('kind', k), p.delete('page'), p))}
        />
        {userId && (
          <button onClick={() => setParams((p) => (p.delete('userId'), p))} className="ml-auto text-sm text-gold-300 hover:underline">
            Только игры игрока · сбросить
          </button>
        )}
      </div>

      <Card className="overflow-hidden">
        {games.isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : games.isError ? (
          <div className="p-4">
            <Alert>Не удалось загрузить матчи</Alert>
          </div>
        ) : games.data!.items.length === 0 ? (
          <Empty title="Матчей нет" />
        ) : (
          <div className="overflow-x-auto">
            <table className="table-base">
              <thead>
                <tr>
                  <th>Начало</th>
                  <th>Комната</th>
                  <th>Игроков</th>
                  <th>Дней</th>
                  <th>Результат</th>
                </tr>
              </thead>
              <tbody>
                {games.data!.items.map((g) => (
                  <tr key={g.id} className="hover:bg-white/[.02]">
                    <td className="whitespace-nowrap">
                      {g.status === 'IN_PROGRESS' ? (
                        <span className="text-zinc-400" title="Журнал будет доступен после окончания игры">
                          {formatDate(g.startedAt)}
                        </span>
                      ) : (
                        <Link to={`/admin/games/${g.id}`} className="text-gold-300 hover:underline">
                          {formatDate(g.startedAt)}
                        </Link>
                      )}
                    </td>
                    <td className="text-zinc-400">
                      {g.roomName ?? '—'} <KindBadge kind={g.kind} />
                    </td>
                    <td>{g.playerCount}</td>
                    <td>{g.daysPlayed}</td>
                    <td>
                      {g.status === 'IN_PROGRESS' ? (
                        <Badge tone="blue">идёт</Badge>
                      ) : g.status === 'ABORTED' ? (
                        <Badge>прервана</Badge>
                      ) : (
                        <Badge tone={g.winner === 'MAFIA' ? 'red' : g.winner === 'TOWN' ? 'green' : 'gold'}>{g.winner ? teamName[g.winner] : '—'}</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {games.data && (
        <Pagination page={games.data.page} pageSize={games.data.pageSize} total={games.data.total} onPage={(n) => setParams((p) => (p.set('page', String(n)), p))} />
      )}
    </div>
  );
}
